<template>
  <div>
    <menu-content-container
      :header="$t('forms.content')"
      :padding-left-right="8"
    >
      <content-row justify-between>
        <TInput
          v-model="content"
          data-testid="loop-button-text"
          :placeholder="$t('forms.btn_text_placeholder')"
        />
      </content-row>
    </menu-content-container>
    <menu-content-container :padding-left-right="8">
      <TLabel
        :changed="isChanged('align') && !fullWidth"
        :tooltip="$t('forms.reset_value')"
        @reset="resetChildAttr('align')"
        >{{ $t("forms.button_align") }}</TLabel
      >
      <content-row justify-between :margin-top="4">
        <TButtonGroup data-testid="loop-align-button-group">
          <TGroupButton
            data="left"
            data-testid="loop-button-align-left"
            small
            :disabled="fullWidth"
            :active="align === 'left'"
            @button-clicked="align = 'left'"
          >
            <AligmentLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="loop-button-align-center"
            small
            :disabled="fullWidth"
            :active="align === 'center'"
            @button-clicked="align = 'center'"
          >
            <AligmentCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="loop-button-align-right"
            small
            :disabled="fullWidth"
            :active="align === 'right'"
            @button-clicked="align = 'right'"
          >
            <AligmentRight />
          </TGroupButton>
        </TButtonGroup>
        <t-switch
          v-model="fullWidth"
          :label="$t('forms.full_width')"
          data-testid="full-width-switch"
          :changed="isFullWidthChanged"
          :reset-tooltip="$t('forms.reset_value')"
          @reset="resetFullWidth"
        ></t-switch>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_style')"
      :padding-left-right="8"
    >
      <content-row>
        <color-picker
          data-testid="loop-button-color"
          :label="$t('product.btn_color')"
          :default-color="bgColor"
          :changed="isChanged('background-color')"
          :reset-tooltip="$t('forms.reset_value')"
          @color-change="bgColor = $event"
          @reset="resetChildAttr('background-color')"
        ></color-picker>
        <TButton
          v-if="!gradient"
          data-testid="button-add-gradient"
          style="align-self: flex-end"
          @click="addGradient"
        >
          <template #icon>
            <svg
              width="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                fill="currentColor"
              />
            </svg>
          </template>
          {{ $t("forms.add_gradient") }}
        </TButton>
      </content-row>
      <content-row class="!block" data-testid="gradient-form">
        <TTabsToggle
          v-if="gradient"
          :header="$t('forms.gradient')"
          default-open
          no-move
          show-remove
          @remove-block="gradient = undefined"
        >
          <menu-content-container border-full :padding-left-right="8">
            <TGradient v-model="gradient" />
          </menu-content-container>
        </TTabsToggle>
      </content-row>

      <content-row :margin-bottom="16">
        <t-note>{{ $t("layout.background_gradient_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <menu-content-container
      :header="$t('forms.button_hover')"
      :padding-left-right="8"
      border-full
    >
      <template #header-action>
        <div
          class="flex cursor-pointer items-center font-inter text-xs font-medium leading-6 text-accent-1 transition duration-300 hover:opacity-80"
          data-testid="toggle-hover-button"
          @click="toggleHover"
        >
          <div>
            {{ !hover ? $t("forms.add_hover") : $t("forms.remove_hover") }}
          </div>
          <PlusIcon v-if="!hover" />
          <Minus v-else />
        </div>
      </template>
      <transition name="slide-fade-borders" mode="out-in">
        <div v-if="hover">
          <content-row>
            <color-picker
              :label="$t('forms.button_color_hover')"
              data-testid="hover-button-color"
              position="left"
              :default-color="hoverBgColor"
              :changed="isHoverPropChanged('background-color')"
              :reset-tooltip="$t('forms.reset_value')"
              @color-change="hoverBgColor = $event"
              @reset="resetHoverProp('background-color')"
            ></color-picker>
            <color-picker
              :label="$t('forms.button_text_color_hover')"
              data-testid="hover-button-text-color"
              position="right"
              :default-color="hoverColor"
              :changed="isHoverPropChanged('color')"
              :reset-tooltip="$t('forms.reset_value')"
              @color-change="hoverColor = $event"
              @reset="resetHoverProp('color')"
            ></color-picker>
          </content-row>
          <content-row class="!block">
            <TButton
              v-if="!hoverGradient"
              data-testid="add-hover-gradient"
              @click="addGradientHover"
            >
              <template #icon>
                <svg
                  width="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M19.958 3C20.3669 3 20.7597 3.16205 21.0488 3.45117C21.3379 3.74029 21.5 4.13312 21.5 4.54199V19.958C21.5 20.3669 21.3379 20.7597 21.0488 21.0488C20.7597 21.3379 20.3669 21.5 19.958 21.5H4.54199C4.13312 21.5 3.74029 21.3379 3.45117 21.0488C3.16205 20.7597 3 20.3669 3 19.958V4.54199C3 4.13312 3.16205 3.74029 3.45117 3.45117C3.74029 3.16205 4.13312 3 4.54199 3H19.958ZM10.708 4.54199V7.625H7.625V10.708H10.708V13.792H7.625V16.875H10.708V19.958H13.792V16.875H16.875V19.958H19.958V4.54199H16.875V7.625H13.792V4.54199H10.708ZM13.792 16.875H10.708V13.792H13.792V16.875ZM16.875 13.792H13.792V10.708H16.875V13.792ZM13.792 10.708H10.708V7.625H13.792V10.708Z"
                    fill="currentColor"
                  />
                </svg>
              </template>
              {{ $t("forms.add_gradient") }}
            </TButton>
            <TTabsToggle
              v-if="hoverGradient"
              :header="$t('forms.gradient_hover')"
              :changed="isHoverPropChanged('gradient')"
              data-testid="hover-gradient-form"
              default-open
              no-move
              show-remove
              can-be-reset
              @remove-block="hoverGradient = 'none'"
              @reset="resetHoverProp('gradient')"
            >
              <menu-content-container border-full :padding-left-right="8">
                <TGradient v-model="hoverGradient" />
              </menu-content-container>
            </TTabsToggle>
          </content-row>
        </div>
      </transition>

      <content-row :margin-top="12">
        <t-note>{{ $t("layout.hover_text") }}</t-note>
      </content-row>
    </menu-content-container>

    <borderRadiusForm
      data-testid="loop-button-radius"
      :label="$t('forms.button_border')"
      :padding-left-right="8"
      external-usage
      :external-border-radius="borderRadius"
      :changed="isChanged('border-radius')"
      :default-value="getChildDefault('border-radius')"
      @borderRadiusChanged="onBorderRadiusChanged"
      @reset="resetChildAttr('border-radius')"
    ></borderRadiusForm>

    <menu-content-container :padding-left-right="8">
      <FontSizeColor
        :block="block"
        :block-index="blockIndex"
        :col-index="colIndex"
        data-testid="loop-button-settings"
      />
      <content-row :margin-top="8">
        <TButtonGroup data-testid="loop-text-align-button-group">
          <TGroupButton
            data="left"
            data-testid="loop-button-text-align-left"
            small
            :active="textAlign === 'left'"
            @button-clicked="textAlign = 'left'"
          >
            <TextLeft />
          </TGroupButton>
          <TGroupButton
            data="center"
            data-testid="loop-button-text-align-center"
            small
            :active="textAlign === 'center'"
            @button-clicked="textAlign = 'center'"
          >
            <TextCenter />
          </TGroupButton>
          <TGroupButton
            data="right"
            data-testid="loop-button-text-align-right"
            small
            :active="textAlign === 'right'"
            @button-clicked="textAlign = 'right'"
          >
            <TextRight />
          </TGroupButton>
        </TButtonGroup>
      </content-row>
    </menu-content-container>

    <paddingForm
      data-testid="loop-padding-form"
      :label="$t('forms.button_padding')"
      :input-label="$t('forms.padding_label')"
      :toggle-label="$t('forms.independent_paddings')"
      :padding-left-right="8"
      external-usage
      :external-padding="innerPadding"
      :changed="isChanged('inner-padding')"
      :default-value="getChildDefault('inner-padding')"
      @paddingChanged="onInnerPaddingChanged"
      @reset="resetChildAttr('inner-padding')"
    ></paddingForm>

    <paddingForm
      data-testid="loop-button-margin"
      :padding-left-right="8"
      :border-bottom="false"
      class="!mb-2"
      external-usage
      :external-padding="padding"
      :changed="isChanged('padding')"
      :default-value="getChildDefault('padding')"
      @paddingChanged="onPaddingChanged"
      @reset="resetChildAttr('padding')"
    ></paddingForm>
  </div>
</template>

<script lang="ts" setup>
  import colorPicker from "../elements/colorPicker.vue";
  import paddingForm from "../paddingForm.vue";
  import borderRadiusForm from "../borderRadiusForm.vue";
  import FontSizeColor from "./fontSizeColorLoop.vue";
  import TGradient from "@/components/TGradient/TGradient.vue";

  import { useMjmlStore } from "@/stores/mjmlStore";
  import { TopolLoopButton } from "@/schemas/template/blocks/loopBlockSchema";
  import { normalizeSpacing } from "@/utils/spacingUtils";

  import {
    TButtonGroup,
    TGroupButton,
    TSwitch,
    TInput,
    TTabsToggle,
    TNote,
    TButton,
    TLabel,
  } from "@topol/twig-ui";
  import {
    AligmentLeft,
    AligmentCenter,
    AligmentRight,
    TextLeft,
    TextCenter,
    TextRight,
    PlusIcon,
    Minus,
  } from "@topol/icons";
  import { computed } from "vue";

  const props = defineProps<{
    colIndex: number;
    blockIndex: number;
    block: TopolLoopButton;
  }>();

  const mjmlStore = useMjmlStore();

  // --- Reset helpers ---
  function getChildDefault(prop: string) {
    const baseValues = mjmlStore.getBaseAttrWithDefault("mj-button", prop);
    return baseValues?.value ?? baseValues?.defaultValue;
  }

  function isChanged(prop: string) {
    const currentValue =
      props.block.attributes[prop as keyof typeof props.block.attributes];
    const defaultValue = getChildDefault(prop);
    if (currentValue === undefined || defaultValue === undefined) return false;
    if (typeof currentValue === "string" && typeof defaultValue === "string") {
      const normCurrent = normalizeSpacing(currentValue);
      const normDefault = normalizeSpacing(defaultValue);
      if (normCurrent && normDefault) return normCurrent !== normDefault;
    }
    return String(currentValue) !== String(defaultValue);
  }

  function resetChildAttr(prop: string) {
    const defaultValue = getChildDefault(prop);
    mjmlStore.updateLoopBlockChildAttribute({
      columnIndex: props.colIndex,
      blockIndex: props.blockIndex,
      prop,
      value: defaultValue,
    });
  }

  const isFullWidthChanged = computed(() => {
    const widthValue = props.block.attributes["width"];
    return widthValue !== undefined && widthValue !== null;
  });

  function isHoverPropChanged(prop: string): boolean {
    if (!props.block.hover) return false;
    const baseHover = mjmlStore.getBaseAttr("mj-button", "hover") as
      | Record<string, string>
      | undefined;
    const localVal = (props.block.hover as Record<string, string>)[prop];
    const baseVal = baseHover?.[prop];
    if (baseVal !== undefined) {
      return localVal !== undefined && localVal !== baseVal;
    }
    return localVal !== undefined;
  }

  function resetHoverProp(prop: string) {
    if (!props.block.hover) return;
    const baseHover = mjmlStore.getBaseAttr("mj-button", "hover") as
      | Record<string, string>
      | undefined;
    const baseVal = baseHover?.[prop];
    const newHover: Record<string, string> = { ...props.block.hover };
    if (baseVal !== undefined) {
      newHover[prop] = baseVal;
    } else {
      delete newHover[prop];
    }
    mjmlStore.updateLoopBlockChildProp({
      columnIndex: props.colIndex,
      blockIndex: props.blockIndex,
      prop: "hover",
      value: newHover,
    });
  }

  function resetFullWidth() {
    mjmlStore.updateLoopBlockChildAttribute({
      columnIndex: props.colIndex,
      blockIndex: props.blockIndex,
      prop: "width",
      value: undefined,
    });
  }

  const block = computed(() => {
    return props.block;
  });

  const content = computed({
    get() {
      return block.value.content || "";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildProp({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "content",
        value: val,
      });
    },
  });

  const color = computed({
    get() {
      return block.value.attributes.color || "";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "color",
        value: val,
      });
    },
  });

  const bgColor = computed({
    get() {
      return block.value.attributes["background-color"] || "";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "background-color",
        value: val,
      });
    },
  });

  const gradient = computed({
    get() {
      return block.value.attributes["gradient"];
    },
    set(val?: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "gradient",
        value: val,
      });
    },
  });

  const hover = computed({
    get() {
      return block.value.hover;
    },
    set(val?: Record<string, string>) {
      mjmlStore.updateLoopBlockChildProp({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "hover",
        value: val,
      });
    },
  });

  const hoverBgColor = computed({
    get() {
      return hover.value?.["background-color"] || bgColor.value;
    },
    set(val: string) {
      const newHover = { ...hover.value, "background-color": val };
      hover.value = newHover;
    },
  });

  const hoverColor = computed({
    get() {
      return hover.value?.color || color.value;
    },
    set(val: string) {
      const newHover = { ...hover.value, color: val };
      hover.value = newHover;
    },
  });

  const hoverGradient = computed({
    get() {
      if (hover.value?.gradient === "none") {
        return undefined;
      }
      return hover.value?.gradient || gradient.value;
    },
    set(val?: string) {
      if (!val) {
        const newHover = { ...hover.value };
        delete newHover.gradient;
        hover.value = newHover;
        return;
      }
      const newHover = { ...hover.value, gradient: val };
      hover.value = newHover;
    },
  });

  const align = computed({
    get() {
      return block.value.attributes.align || "center";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "align",
        value: val,
      });
    },
  });

  const textAlign = computed({
    get() {
      return block.value.attributes["text-align"] || "center";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "text-align",
        value: val,
      });
    },
  });

  const fullWidth = computed({
    get() {
      return block.value.attributes["width"] === "100%";
    },
    set(val: boolean) {
      let value = "100%";
      if (val == false) {
        value = "auto";
      } else {
        mjmlStore.updateLoopBlockChildAttribute({
          columnIndex: props.colIndex,
          blockIndex: props.blockIndex,
          prop: "align",
          value: "center",
        });
      }
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "width",
        value: value,
      });
    },
  });

  const borderRadius = computed({
    get() {
      return block.value.attributes["border-radius"];
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "border-radius",
        value: val,
      });
    },
  });

  const innerPadding = computed({
    get() {
      return block.value.attributes["inner-padding"] || "0px";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "inner-padding",
        value: val,
      });
    },
  });

  const padding = computed({
    get() {
      return block.value.attributes.padding || "0px";
    },
    set(val: string) {
      mjmlStore.updateLoopBlockChildAttribute({
        columnIndex: props.colIndex,
        blockIndex: props.blockIndex,
        prop: "padding",
        value: val,
      });
    },
  });

  function onInnerPaddingChanged(val: string) {
    innerPadding.value = val;
  }

  function onPaddingChanged(val: string) {
    padding.value = val;
  }

  function onBorderRadiusChanged(val: string) {
    borderRadius.value = val;
  }

  function addGradient() {
    gradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  function addGradientHover() {
    hoverGradient.value = "linear-gradient(90deg, #ff7e5f 0%, #feb47b 100%)";
  }

  function toggleHover() {
    if (hover.value) {
      hover.value = undefined;
    } else {
      hover.value = {};
    }
  }
</script>
